import classNames from 'classnames';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageFeedback, MessageFeedbackValue } from '@/Domain/api/Conversation/dto/Message.dto';
import { MessageFeedbackPopover } from '@/Domain/components/Inbox/Message/MessageFeedbackPopover';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useMessageFeedback } from '@/Domain/hooks/Conversation/useMessageFeedback.hook';

type Props = {
  conversationId: string;
  messageId: string;
  existingFeedback: MessageFeedback | null;
  nextStepsContext?: 'simulation';
  onFeedbackChanged?: () => void;
};

export const MessageFeedbackButton = ({
  conversationId,
  messageId,
  existingFeedback,
  nextStepsContext,
  onFeedbackChanged,
}: Props) => {
  const { t } = useTranslation('inbox_v2');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<MessageFeedbackValue>('positive');
  const [sessionRating, setSessionRating] = useState<MessageFeedbackValue | null | undefined>(
    undefined,
  );
  const anchorRef = useRef<HTMLDivElement>(null);

  const { submitFeedback, deleteFeedback, isSubmitting, isDeleting } = useMessageFeedback({
    conversationId,
    messageId,
    onSuccess: onFeedbackChanged,
  });

  const savedRating =
    sessionRating === undefined ? (existingFeedback?.value ?? null) : sessionRating;

  const openDetails = (type: MessageFeedbackValue) => {
    setFeedbackType(type);
    setPopoverOpen(true);
  };

  const handleFeedbackClick = (type: MessageFeedbackValue) => {
    if (isSubmitting || isDeleting) return;

    if (savedRating === type) {
      openDetails(type);
      return;
    }

    submitFeedback({ value: type, comment: null }, savedRating !== null, {
      showSuccessToast: false,
      onSuccess: () => {
        setSessionRating(type);
        openDetails(type);
      },
    });
  };

  const handleDetailsSubmit = (comment: string | null) => {
    submitFeedback({ value: feedbackType, comment }, savedRating !== null, {
      onSuccess: () => {
        setSessionRating(feedbackType);
        setPopoverOpen(false);
      },
    });
  };

  const handleDelete = () => {
    deleteFeedback({
      onSuccess: () => {
        setSessionRating(null);
        setPopoverOpen(false);
      },
    });
  };

  const hasPositiveFeedback = savedRating === 'positive';
  const hasNegativeFeedback = savedRating === 'negative';

  return (
    <div className="flex items-center gap-0.5" ref={anchorRef}>
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            variant="ghost"
            size="xs"
            className={classNames({
              'text-emerald-600': hasPositiveFeedback,
            })}
            onClick={() => handleFeedbackClick('positive')}
            data-testid={`message-feedback-positive-${messageId}`}
          >
            <ThumbsUp
              className={classNames('size-3.5', {
                'fill-emerald-600': hasPositiveFeedback,
              })}
            />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {hasPositiveFeedback
            ? t('message_feedback.tooltip.edit_positive')
            : t('message_feedback.tooltip.positive')}
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            variant="ghost"
            size="xs"
            className={classNames({
              'text-rose-500': hasNegativeFeedback,
            })}
            onClick={() => handleFeedbackClick('negative')}
            data-testid={`message-feedback-negative-${messageId}`}
          >
            <ThumbsDown
              className={classNames('size-3.5', {
                'fill-rose-500': hasNegativeFeedback,
              })}
            />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {hasNegativeFeedback
            ? t('message_feedback.tooltip.edit_negative')
            : t('message_feedback.tooltip.negative')}
        </TooltipContent>
      </Tooltip>

      <MessageFeedbackPopover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        feedbackType={feedbackType}
        existingFeedback={existingFeedback}
        anchorRef={anchorRef}
        nextStepsContext={nextStepsContext}
        isSubmitting={isSubmitting}
        isDeleting={isDeleting}
        onSubmit={handleDetailsSubmit}
        onDelete={handleDelete}
      />
    </div>
  );
};
